import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { firmwareAPI, unitAPI } from '../services/api';
import { Package, Search, Trash2, Rocket, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { AdminModalSurface } from './AdminModal';

export default function FirmwareList() {
  const { t } = useTranslation();
  const [firmwareList, setFirmwareList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [selectedFirmware, setSelectedFirmware] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchFirmwareList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await firmwareAPI.getFirmwareList();
      if (response.data?.success) {
        setFirmwareList(response.data.data || []);
        setMessage(null); // 清除之前的错误消息
      }
    } catch (error) {
      console.error('[Firmware List] Error:', error);

      // 添加友好的错误提示
      if (error.response?.status === 403) {
        setMessage({
          type: 'error',
          text: t('firmware.list.permissionError')
        });
      } else {
        setMessage({
          type: 'error',
          text: t('firmware.list.loadError')
        });
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchFirmwareList();
  }, [fetchFirmwareList]);

  const handleDelete = async (firmwareId) => {
    if (!window.confirm(t('firmware.list.deleteConfirm'))) {
      return;
    }

    try {
      const response = await firmwareAPI.deleteFirmware(firmwareId);
      if (response.data?.success) {
        setMessage({ type: 'success', text: t('firmware.list.deleteSuccess') });
        fetchFirmwareList();
      } else {
        setMessage({ type: 'error', text: t('firmware.list.deleteFailed') });
      }
    } catch (error) {
      console.error('[Firmware Delete] Error:', error);
      const errMsg = error.response?.data?.message || '';
      if (errMsg.includes('active upgrade tasks')) {
        if (window.confirm('该固件有进行中的升级任务，需先强制取消所有任务才能删除，是否继续？')) {
          try {
            const forceResp = await firmwareAPI.deleteFirmware(firmwareId, { force: 'true' });
            if (forceResp.data?.success) {
              setMessage({ type: 'success', text: '固件及关联升级任务已强制删除' });
              fetchFirmwareList();
            } else {
              setMessage({ type: 'error', text: t('firmware.list.deleteFailed') });
            }
          } catch (forceError) {
            console.error('[Firmware Force Delete] Error:', forceError);
            setMessage({ type: 'error', text: t('firmware.list.deleteFailed') });
          }
        }
      } else {
        setMessage({ type: 'error', text: t('firmware.list.deleteFailed') });
      }
    }
  };

  const handleDeploy = (firmware) => {
    setSelectedFirmware(firmware);
    setShowDeployModal(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1024 / 1024).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-emerald-400/10', text: 'text-emerald-400', label: t('firmware.list.status.active') },
      testing: { bg: 'bg-blue-400/10', text: 'text-blue-400', label: t('firmware.list.status.testing') },
      deprecated: { bg: 'bg-slate-400/10', text: 'text-slate-400', label: t('firmware.list.status.deprecated') }
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredFirmware = firmwareList.filter(
    (fw) =>
      fw.version?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.deviceModel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fw.crc32?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: firmwareList.length,
    active: firmwareList.filter((fw) => fw.isActive !== false).length,
    testing: firmwareList.filter((fw) => fw.status === 'testing').length
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{t('firmware.list.title')}</h1>
        <p className="text-slate-400 mt-1">{t('firmware.list.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-800 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('firmware.list.stats.total')}</p>
              <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
            </div>
            <Package className="w-8 h-8 text-cyan-400" />
          </div>
        </div>
        <div className="bg-slate-800 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('firmware.list.stats.active')}</p>
              <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.active}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
        </div>
        <div className="bg-slate-800 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('firmware.list.stats.testing')}</p>
              <p className="text-2xl font-bold text-blue-400 mt-1">{stats.testing}</p>
            </div>
            <Loader2 className="w-8 h-8 text-blue-400" />
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('firmware.list.searchPlaceholder')}
          className="w-full bg-slate-800 border border-white/10 rounded-lg pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
        />
      </div>

      {/* Message */}
      {message && (
        <div
          className={`flex items-center gap-3 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-400/10 border border-emerald-400/20'
              : 'bg-rose-400/10 border border-rose-400/20'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          )}
          <p
            className={`text-sm ${
              message.type === 'success' ? 'text-emerald-400' : 'text-rose-400'
            }`}
          >
            {message.text}
          </p>
        </div>
      )}

      {/* Firmware Table */}
      <div className="bg-slate-800 border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : filteredFirmware.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">{t('firmware.list.noFirmware')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900/50 border-b border-white/5">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.list.table.version')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.list.table.model')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.list.table.commandParams', { defaultValue: '文件 / CRC' })}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.list.table.fileSize')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.list.table.uploadDate')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.list.table.status')}
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">
                    {t('firmware.list.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredFirmware.map((firmware) => (
                  <tr key={firmware.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-white font-medium">{firmware.version}</p>
                      {firmware.description && (
                        <p className="text-slate-400 text-sm mt-1">{firmware.description}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-300">{firmware.deviceModel}</td>
                    <td className="px-6 py-4 text-slate-300">
                      <p className="max-w-[260px] break-all font-mono text-xs text-white">{firmware.fileName || '-'}</p>
                      <p className="mt-1 break-all font-mono text-xs text-cyan-300">CRC: {firmware.crc32 || '-'}</p>
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {formatFileSize(firmware.fileSize)}
                    </td>
                    <td className="px-6 py-4 text-slate-300">
                      {formatDate(firmware.createdAt || firmware.uploadDate)}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(firmware.status || (firmware.isActive === false ? 'deprecated' : 'active'))}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleDeploy(firmware)}
                          className="px-3 py-1.5 bg-cyan-400/10 text-cyan-400 rounded-lg hover:bg-cyan-400/20 transition-colors text-sm font-medium flex items-center gap-1.5"
                        >
                          <Rocket className="w-4 h-4" />
                          {t('firmware.list.deploy')}
                        </button>
                        <button
                          onClick={() => handleDelete(firmware.id)}
                          className="px-3 py-1.5 bg-rose-400/10 text-rose-400 rounded-lg hover:bg-rose-400/20 transition-colors text-sm font-medium flex items-center gap-1.5"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t('firmware.list.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deploy Modal */}
      {showDeployModal && selectedFirmware && (
        <DeployModal
          firmware={selectedFirmware}
          onClose={() => {
            setShowDeployModal(false);
            setSelectedFirmware(null);
          }}
        />
      )}
    </div>
  );
}

function DeployModal({ firmware, onClose }) {
  const { t } = useTranslation();
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [step, setStep] = useState('select');
  const [confirmed, setConfirmed] = useState(false);
  const [deploymentResult, setDeploymentResult] = useState(null);
  const modalScrollRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (modalScrollRef.current) modalScrollRef.current.scrollTop = 0;
  }, [step]);

  useEffect(() => {
    const fetchUnits = async () => {
      setLoading(true);
      try {
        const res = await unitAPI.getUnits();
        if (res.data && res.data.success) {
          setUnits(res.data.data);
        }
      } catch (error) {
        console.error('Failed to load units:', error);
        setUnits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, []);

  const filteredUnits = units.filter(unit =>
    unit.deviceId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    unit.deviceName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedUnit = useMemo(
    () => units.find(unit => selectedUnits.includes(unit.id)) || null,
    [selectedUnits, units]
  );

  const selectedStatus = selectedUnit?.computedStatus || selectedUnit?.status || 'Offline';
  const selectedIsOnline = Boolean(selectedUnit && (
    selectedUnit.tcpConnected === true
    || (selectedUnit.tcpConnected == null && selectedStatus === 'Online')
  ));

  const commandPreview = useMemo(() => ({
    Cmd: 'UpgradeVer',
    Crc: String(firmware.crc32 || ''),
    RC: '<服务器确认下发时按设备序列号 +1>',
    Size: String(firmware.fileSize || ''),
    Ver: firmware.fileName || firmware.version || ''
  }), [firmware]);

  const toggleUnit = (unitId) => {
    setSelectedUnits(prev => prev.includes(unitId) ? [] : [unitId]);
    setConfirmed(false);
    setDeploymentResult(null);
  };

  const handleReview = () => {
    if (!selectedUnit) {
      setDeploymentResult({ type: 'error', text: t('firmware.deploy.noUnitsSelected') });
      return;
    }
    if (!selectedIsOnline) {
      setDeploymentResult({
        type: 'error',
        text: t('firmware.deploy.offlineBlocked', { defaultValue: '该设备当前没有真实 TCP 连接，不能进入下发确认。' })
      });
      return;
    }
    setDeploymentResult(null);
    setStep('confirm');
  };

  const handleDeploy = async () => {
    if (!selectedUnit || !selectedIsOnline || !confirmed) return;

    setDeploying(true);
    setDeploymentResult(null);

    try {
      const requestPayload = {
        firmwareId: firmware.id,
        unitIds: [selectedUnit.id]
      };
      const response = await firmwareAPI.triggerUpgrade(requestPayload);

      if (response.data?.success) {
        const data = response.data.data || {};
        const actualCommand = data.commandResults?.find(item => item.success)?.command || null;
        if (data.sentCount > 0) {
          setDeploymentResult({
            type: 'success',
            text: t('firmware.deploy.tcpSent', { defaultValue: '升级任务已建立，UpgradeVer 已写入设备 TCP 连接。' }),
            command: actualCommand,
            submitted: true
          });
        } else if (data.offlineCount > 0) {
          setDeploymentResult({
            type: 'warning',
            text: t('firmware.deploy.queuedOffline', { defaultValue: '任务已建立，但设备已离线，命令尚未发送。' }),
            submitted: true
          });
        } else {
          setDeploymentResult({
            type: 'error',
            text: t('firmware.deploy.createdNotSent', { defaultValue: '任务已建立，但服务器没有确认命令写入 TCP。请到升级监控检查。' }),
            submitted: true
          });
        }
      } else {
        setDeploymentResult({
          type: 'error',
          text: `${t('firmware.deploy.deployFailed')}${response.data?.message ? `: ${response.data.message}` : ''}`
        });
      }
    } catch (error) {
      let errorMessage = t('firmware.deploy.deployFailed');
      if (error.response?.status === 409) {
        const active = error.response?.data?.data?.activeTasks?.[0];
        errorMessage = t('firmware.deploy.activeTaskConflict', {
          defaultValue: `设备已有进行中的升级任务${active?.taskId ? `（任务 #${active.taskId}）` : ''}，请先到升级监控处理。`
        });
      } else if (error.response?.data?.message) {
        errorMessage += `: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }
      setDeploymentResult({ type: 'error', text: errorMessage });
    } finally {
      setDeploying(false);
    }
  };

  return (
    <AdminModalSurface onClose={onClose} surfaceRef={modalScrollRef}>
      <div className="my-2 w-full max-w-2xl rounded-2xl border border-white/10 bg-slate-800 sm:max-h-[90vh] sm:overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">{t('firmware.deploy.title')}</h2>
          <p className="text-slate-400 mt-1">
            {t('firmware.deploy.subtitle', { version: firmware.version })}
          </p>
        </div>

        <div className="p-6 space-y-4">
          {step === 'select' ? (
            <>
              <div className="rounded-lg border border-amber-400/40 bg-amber-100 p-3 text-sm font-medium leading-6 text-amber-900">
                {t('firmware.deploy.singleOnlineOnly', { defaultValue: '安全测试模式：一次只能选择一台真实 TCP 在线设备。第一步只选设备，不会建立任务。' })}
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('firmware.deploy.searchPlaceholder')}
                  className="min-w-0 flex-1 rounded-lg border border-white/10 bg-slate-900 px-4 py-2 text-white placeholder-slate-500 focus:border-cyan-400 focus:outline-none"
                />
                <div className="text-sm text-slate-400">
                  {t('firmware.deploy.selectedCount', { count: selectedUnits.length })}
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto rounded-lg border border-white/10 bg-slate-900">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
                  </div>
                ) : filteredUnits.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">{t('firmware.deploy.noUnits')}</div>
                ) : (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-800/95">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">
                          {t('firmware.deploy.selectOne', { defaultValue: '选择' })}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">{t('firmware.deploy.deviceId')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">{t('firmware.deploy.deviceName')}</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">{t('firmware.deploy.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {filteredUnits.map((unit) => {
                        const status = unit.computedStatus || unit.status || 'Offline';
                        const tcpOnline = unit.tcpConnected === true || (unit.tcpConnected == null && status === 'Online');
                        return (
                          <tr key={unit.id} className={tcpOnline ? 'transition-colors hover:bg-white/5' : 'opacity-55'}>
                            <td className="px-4 py-3">
                              <input
                                type="radio"
                                name="firmware-target-unit"
                                checked={selectedUnits.includes(unit.id)}
                                onChange={() => toggleUnit(unit.id)}
                                disabled={!tcpOnline}
                                aria-label={`${t('firmware.deploy.selectOne', { defaultValue: '选择设备' })} ${unit.deviceId}`}
                              />
                            </td>
                            <td className="break-all px-4 py-3 font-mono text-sm text-white">{unit.deviceId}</td>
                            <td className="px-4 py-3 text-sm text-slate-300">{unit.deviceName || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`rounded-full px-2 py-1 text-xs font-medium ${tcpOnline ? 'bg-emerald-400/10 text-emerald-400' : 'bg-rose-400/10 text-rose-300'}`}>
                                {tcpOnline
                                  ? t('firmware.deploy.tcpOnline', { defaultValue: 'TCP 在线' })
                                  : t('firmware.deploy.tcpOffline', { defaultValue: 'TCP 离线（不可选）' })}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="rounded-lg border border-rose-300 bg-rose-100 p-4">
                <p className="font-bold text-rose-950">{t('firmware.deploy.finalReviewTitle', { defaultValue: '最终下发确认（第二步）' })}</p>
                <p className="mt-1 text-sm font-medium leading-6 text-rose-900">{t('firmware.deploy.finalReviewHint', { defaultValue: '点击确认后会立即建立真实升级任务并向这台设备发送命令，请逐项核对。' })}</p>
              </div>

              <dl className="grid gap-3 rounded-lg border border-white/10 bg-slate-900 p-4 text-sm sm:grid-cols-2">
                <div><dt className="text-slate-400">{t('firmware.deploy.deviceId')}</dt><dd className="mt-1 break-all font-mono text-white">{selectedUnit?.deviceId || '-'}</dd></div>
                <div><dt className="text-slate-400">{t('firmware.deploy.tcpStatus', { defaultValue: 'TCP 状态' })}</dt><dd className="mt-1 font-bold text-emerald-300">{selectedIsOnline ? t('firmware.deploy.tcpOnline', { defaultValue: 'TCP 在线' }) : t('firmware.deploy.tcpOffline', { defaultValue: 'TCP 离线' })}</dd></div>
                <div><dt className="text-slate-400">{t('firmware.deploy.currentVersion', { defaultValue: '设备当前版本' })}</dt><dd className="mt-1 break-all font-mono text-white">{selectedUnit?.firmwareVersion || '-'}</dd></div>
                <div><dt className="text-slate-400">{t('firmware.deploy.targetVersion', { defaultValue: '目标文件 / Ver' })}</dt><dd className="mt-1 break-all font-mono text-white">{firmware.fileName || firmware.version || '-'}</dd></div>
                <div><dt className="text-slate-400">{t('firmware.deploy.fileSize', { defaultValue: '文件大小' })}</dt><dd className="mt-1 font-mono text-white">{firmware.fileSize} bytes</dd></div>
                <div><dt className="text-slate-400">CRC32</dt><dd className="mt-1 break-all font-mono text-cyan-300">{firmware.crc32 || '-'}</dd></div>
              </dl>

              <div>
                <p className="mb-2 text-sm font-bold text-white">{t('firmware.deploy.commandPreview', { defaultValue: 'UpgradeVer 命令预览' })}</p>
                <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-lg border border-cyan-300 bg-cyan-50 p-4 font-mono text-xs font-semibold leading-6 text-cyan-950">{JSON.stringify(commandPreview)}</pre>
                <p className="mt-2 text-xs leading-5 text-slate-400">{t('firmware.deploy.rcHint', { defaultValue: 'RC 不固定为 4206；服务器在真正发送时读取该设备上一次 RC 并 +1，实际值会在发送结果中显示。' })}</p>
              </div>

              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/10 p-4 text-sm leading-6 text-slate-200">
                <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} className="mt-1" />
                <span>{t('firmware.deploy.confirmCheckbox', { defaultValue: '我已核对设备完整 ID、TCP 在线状态、文件名、Size 与 CRC，确认只向这一台设备下发。' })}</span>
              </label>
            </>
          )}

          {deploymentResult && (
            <div className={`rounded-lg border p-4 text-sm leading-6 ${
              deploymentResult.type === 'success'
                ? 'border-emerald-300 bg-emerald-100 text-emerald-950'
                : deploymentResult.type === 'warning'
                  ? 'border-amber-300 bg-amber-100 text-amber-950'
                  : 'border-rose-300 bg-rose-100 text-rose-950'
            }`} role="status">
              <p className="font-bold">{deploymentResult.text}</p>
              {deploymentResult.command && (
                <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all rounded bg-slate-950/70 p-3 font-mono text-xs text-white">{JSON.stringify(deploymentResult.command)}</pre>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 p-6 sm:flex-row">
          {step === 'confirm' && !deploymentResult?.submitted ? (
            <button
              type="button"
              onClick={() => { setStep('select'); setConfirmed(false); setDeploymentResult(null); }}
              disabled={deploying}
              className="flex-1 rounded-lg bg-slate-700 px-4 py-2.5 text-white transition-colors hover:bg-slate-600 disabled:opacity-50"
            >
              {t('firmware.deploy.backToSelection', { defaultValue: '返回重新选择' })}
            </button>
          ) : (
            <button type="button" onClick={onClose} disabled={deploying} className="flex-1 rounded-lg bg-slate-700 px-4 py-2.5 text-white transition-colors hover:bg-slate-600 disabled:opacity-50">
              {deploymentResult?.submitted ? t('firmware.deploy.close', { defaultValue: '关闭（请前往升级监控）' }) : t('firmware.deploy.cancel')}
            </button>
          )}

          {!deploymentResult?.submitted && (
            <button
              type="button"
              onClick={step === 'select' ? handleReview : handleDeploy}
              disabled={deploying || (step === 'select' ? !selectedUnit : !confirmed || !selectedIsOnline)}
              className="flex-1 rounded-lg bg-cyan-400 px-4 py-2.5 font-medium text-slate-900 transition-colors hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {deploying ? (
                <span className="flex items-center justify-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />{t('firmware.deploy.deploying')}</span>
              ) : step === 'select' ? (
                t('firmware.deploy.reviewCommand', { defaultValue: '下一步：核对命令' })
              ) : (
                <span className="flex items-center justify-center gap-2"><Rocket className="h-5 w-5" />{t('firmware.deploy.confirmDeploy', { defaultValue: '确认并真实下发' })}</span>
              )}
            </button>
          )}
        </div>
      </div>
    </AdminModalSurface>
  );
}
